import { useEffect, useCallback, useState, useRef } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, RefreshCw, AlertCircle, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { getInsight, getStateHash } from '../../lib/ai';
import type { TabId } from '../../types';

interface InsightCardProps {
  tabId: string;
}

export function InsightCard({ tabId }: InsightCardProps) {
  const assumptions = useSimulatorStore(s => s.assumptions);
  const packages = useSimulatorStore(s => s.packages);
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);
  const cached = useSimulatorStore(s => s.insightCache[tabId]);
  const setCached = useSimulatorStore(s => s.setCachedInsight);
  const expanded = useSimulatorStore(s => s.insightExpandedTabs[tabId] ?? false);
  const setExpanded = useSimulatorStore(s => s.setInsightExpanded);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const state = { assumptions, packages, activeTab: tabId as TabId, selectedPrice };
  const hash = getStateHash(state);
  const hashRef = useRef(hash);
  hashRef.current = hash;

  const isFresh = cached?.hash === hash;
  const content = cached?.content || '';
  const hasContent = !!content;
  const isStale = hasContent && !isFresh;

  const fetchInsight = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const store = useSimulatorStore.getState();
      const fetchState = {
        assumptions: store.assumptions,
        packages: store.packages,
        activeTab: tabId as TabId,
        selectedPrice: store.selectedPrice,
      };
      const fetchHash = getStateHash(fetchState);
      const result = await getInsight(tabId, fetchState);
      setCached(tabId, fetchHash, result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load insights');
    }
    setLoading(false);
  }, [tabId]);

  // The InsightPreloader handles background fetching for all tabs.
  // This effect only fires if this specific tab has no content at all (e.g., preloader hasn't finished yet).
  useEffect(() => {
    if (!content && !loading && !error) {
      fetchInsight();
    }
  }, [hash]);

  const toggle = () => setExpanded(tabId, !expanded);

  // Collapsed state
  if (!expanded) {
    const statusText = loading
      ? 'Loading AI Analysis...'
      : isStale
        ? 'Parameters Changed — Click to Review'
        : hasContent
          ? 'AI Analysis Available'
          : error
            ? 'Analysis Failed'
            : 'AI Analysis';

    const statusColor = isStale ? 'text-metric-warning' : hasContent ? 'text-chart-teal' : 'text-muted';
    const borderColor = isStale ? '#F59E0B20' : hasContent ? '#14B8A620' : 'var(--color-border)';
    const bgColor = isStale ? '#F59E0B08' : hasContent ? '#14B8A608' : 'transparent';

    return (
      <button
        onClick={toggle}
        className="mb-4 w-full flex items-center justify-between px-4 py-2.5 rounded-lg transition-all border"
        style={{ borderColor, backgroundColor: bgColor }}
      >
        <div className="flex items-center gap-2">
          {isStale ? <AlertTriangle size={13} className="text-metric-warning" /> : <Sparkles size={13} className={hasContent ? 'text-chart-teal' : 'text-muted'} />}
          <span className={`text-xs font-medium ${statusColor}`}>{statusText}</span>
          {loading && <RefreshCw size={11} className="text-chart-teal animate-spin" />}
          {hasContent && isFresh && !loading && (
            <span className="w-1.5 h-1.5 rounded-full bg-chart-teal animate-pulse" />
          )}
        </div>
        <ChevronRight size={14} className="text-muted" />
      </button>
    );
  }

  // Expanded state
  return (
    <div className="card mb-6 relative" style={{ borderLeft: isStale ? '3px solid #F59E0B' : '3px solid #14B8A6' }}>
      <div className="flex items-center justify-between mb-3">
        <button onClick={toggle} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <ChevronDown size={14} className={isStale ? 'text-metric-warning' : 'text-chart-teal'} />
          <Sparkles size={14} className={isStale ? 'text-metric-warning' : 'text-chart-teal'} />
          <span className={`text-xs font-semibold uppercase tracking-wider ${isStale ? 'text-metric-warning' : 'text-chart-teal'}`}>
            AI Analysis {isStale && '(Updating...)'}
          </span>
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={fetchInsight}
            disabled={loading}
            className="p-1 text-muted hover:text-secondary transition-colors rounded"
            title="Refresh analysis"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={toggle} className="text-micro text-muted hover:text-secondary transition-colors px-1.5">
            Collapse
          </button>
        </div>
      </div>

      {isStale && (
        <div className="flex items-center gap-2 text-detail text-metric-warning mb-3 px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle size={12} />
          <span>Parameters changed. {loading ? 'Refreshing...' : 'This analysis may be outdated.'}</span>
          {!loading && <button onClick={fetchInsight} className="underline ml-1 font-medium">Refresh now</button>}
        </div>
      )}

      {loading && !content && (
        <div className="space-y-2.5">
          <div className="h-3 skeleton w-3/4" />
          <div className="h-3 skeleton w-1/2" />
          <div className="h-3 skeleton w-5/6" />
          <div className="h-3 skeleton w-2/3" />
        </div>
      )}

      {error && !content && (
        <div className="flex items-center gap-2 text-xs text-metric-negative">
          <AlertCircle size={13} />
          <span>{error}</span>
          <button onClick={fetchInsight} className="underline ml-1">Retry</button>
        </div>
      )}

      {content && (
        <div className={`text-sm leading-relaxed text-secondary break-words [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_ol]:mb-2 [&_li]:mb-0.5 [&_strong]:text-primary [&_strong]:font-semibold ${isStale ? 'opacity-70' : ''}`}>
          <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
        </div>
      )}

      {loading && content && (
        <div className="absolute top-3 right-10">
          <RefreshCw size={12} className="text-chart-teal animate-spin" />
        </div>
      )}
    </div>
  );
}
